import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Coins, DollarSign, Wallet, Loader2, Banknote } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLE = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10",
  paid: "text-[#2ecc71] bg-[#2ecc71]/10",
  rejected: "text-[#E63946] bg-[#E63946]/10",
};

export default function Payouts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("cash");
  const [coins, setCoins] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/withdrawals/me").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!destination.trim()) { toast.error("Enter your payout details"); return; }
    const payload = { kind, method, destination };
    if (kind === "coins") {
      const c = parseInt(coins, 10);
      if (!c || c < 1) { toast.error("Enter how many coins to cash out"); return; }
      payload.coins = c;
    } else {
      const a = parseFloat(amount);
      if (!a || a <= 0) { toast.error("Enter a valid amount"); return; }
      payload.amount_usd = a;
    }
    setSubmitting(true);
    try {
      await api.post("/withdrawals/request", payload);
      toast.success("Payout requested — you'll be notified once it's processed 💸");
      setCoins(""); setAmount(""); setDestination("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="p-12 text-[#A0A0A0]">Loading payouts…</div>;

  const coinValue = data.coins_value_usd || 0;

  return (
    <div className="p-6 md:p-12 max-w-3xl mx-auto" data-testid="payouts-page">
      <button data-testid="payouts-back" onClick={() => navigate("/profile")} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-6">
        <ArrowLeft size={18} /> Back to profile
      </button>
      <div className="overline mb-1">Creator payouts</div>
      <h1 className="font-heading text-4xl md:text-5xl mb-2">Withdraw your earnings</h1>
      <p className="text-sm text-[#A0A0A0] mb-8">Cash out your earnings and Empire Coins. Payouts are reviewed and sent by the Empire team.</p>

      {/* Balances */}
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="pe-card rounded-2xl p-6" data-testid="cash-balance-card">
          <div className="flex items-center gap-2 text-[#A0A0A0] text-sm mb-2"><DollarSign size={16} /> Cash earnings</div>
          <div className="font-heading text-3xl gold-text">${data.cash_balance_usd.toFixed(2)}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">From subscriptions & tips</div>
        </div>
        <div className="pe-card rounded-2xl p-6" data-testid="coin-balance-card">
          <div className="flex items-center gap-2 text-[#A0A0A0] text-sm mb-2"><Coins size={16} /> Empire Coins</div>
          <div className="font-heading text-3xl gold-text">{data.coins_balance}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">≈ ${coinValue.toFixed(2)} at ${data.coin_rate.toFixed(2)}/coin</div>
        </div>
      </div>

      {/* Request form */}
      <div className="pe-card rounded-2xl p-6 mb-8">
        <div className="font-heading text-2xl mb-4 flex items-center gap-2"><Wallet size={20} className="text-[#D4AF37]" /> Request a payout</div>
        <div className="flex gap-2 mb-4">
          <button data-testid="kind-cash" onClick={() => setKind("cash")}
            className={`flex-1 rounded-full py-2 text-sm transition-colors ${kind === "cash" ? "gold-gradient text-black" : "btn-outline-gold"}`}>Cash earnings</button>
          <button data-testid="kind-coins" onClick={() => setKind("coins")}
            className={`flex-1 rounded-full py-2 text-sm transition-colors ${kind === "coins" ? "gold-gradient text-black" : "btn-outline-gold"}`}>Empire Coins</button>
        </div>
        <div className="space-y-3">
          {kind === "coins" ? (
            <input data-testid="coins-input" type="number" min="1" value={coins} onChange={(e) => setCoins(e.target.value)}
              placeholder={`Coins to cash out (max ${data.coins_balance})`} className="pe-input w-full rounded-lg px-4 py-3" />
          ) : (
            <input data-testid="amount-input" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`Amount in USD (max $${data.cash_balance_usd.toFixed(2)})`} className="pe-input w-full rounded-lg px-4 py-3" />
          )}
          <div className="flex gap-2">
            <button data-testid="method-paypal" onClick={() => setMethod("paypal")}
              className={`flex-1 rounded-full py-2 text-sm transition-colors ${method === "paypal" ? "gold-gradient text-black" : "btn-outline-gold"}`}>PayPal</button>
            <button data-testid="method-bank" onClick={() => setMethod("bank")}
              className={`flex-1 rounded-full py-2 text-sm transition-colors ${method === "bank" ? "gold-gradient text-black" : "btn-outline-gold"}`}>Bank</button>
          </div>
          <input data-testid="destination-input" value={destination} onChange={(e) => setDestination(e.target.value)}
            placeholder={method === "paypal" ? "Your PayPal email" : "Bank account / reference details"} className="pe-input w-full rounded-lg px-4 py-3" />
          <button data-testid="request-payout-btn" onClick={submit} disabled={submitting}
            className="btn-gold rounded-full px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Banknote size={15} />} {submitting ? "Requesting…" : "Request payout"}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="pe-card rounded-2xl p-6" data-testid="payout-history">
        <div className="font-heading text-xl mb-4">Payout history</div>
        {data.history.length === 0 ? (
          <div className="text-sm text-[#A0A0A0]">No payouts yet.</div>
        ) : (
          <div className="space-y-3">
            {data.history.map((w) => (
              <div key={w.id} data-testid={`payout-row-${w.id}`} className="flex items-center justify-between border-b border-[#2A2A2A] pb-3">
                <div>
                  <div className="text-sm text-white">${w.amount_usd.toFixed(2)} <span className="text-[#A0A0A0]">· {w.kind === "coins" ? `${w.coins} coins` : "cash"} · {w.method}</span></div>
                  <div className="text-xs text-[#A0A0A0]">{w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}{w.note ? ` — ${w.note}` : ""}</div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full capitalize ${STATUS_STYLE[w.status] || "text-[#A0A0A0] bg-white/5"}`}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
